/**
 * Channel list — Toolbar + DataTable with delete confirm.
 * Row click navigates to ChannelDetail.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Bell, Plus, Search } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type StatusTone } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { Button, Badge, Input, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useChannels, useDeleteChannel, type Channel } from '@/api/healthcheck';
import { fmtRelTime } from '../shared';
import { PROVIDER_META } from './channel-provider-meta';
import { RowMenu } from '../../components/RowMenu';

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);

function ChannelTypeBadge({ type }: { type: Channel['type'] }) {
  if (!type) return <Badge tone="neutral" appearance="subtle" size="sm">Multi-type</Badge>;
  const meta = PROVIDER_META[type];
  const Icon = meta.icon;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Icon size={13} style={{ color: 'var(--vn-fg-muted)' }} />
      <Badge tone="neutral" appearance="subtle" size="sm">{meta.label}</Badge>
    </div>
  );
}

export function ChannelList() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const { data: channels = [], isLoading, isError, refetch } = useChannels();
  const del = useDeleteChannel();
  const [pendingDelete, setPendingDelete] = useState<Channel | null>(null);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filteredChannels = q ? channels.filter((c) => c.name.toLowerCase().includes(q)) : channels;

  const columns = useMemo<ColumnDef<Channel, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ fontWeight: 500 }}>{row.original.name}</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => <ChannelTypeBadge type={row.original.type} />,
      },
      {
        id: 'status',
        header: 'Active',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={(row.original.is_active ? 'success' : 'muted') as StatusTone}>
            {row.original.is_active ? 'Active' : 'Paused'}
          </StatusPill>
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => muted(fmtRelTime(row.original.updated_at)),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RowMenu
            items={[
              {
                label: 'Delete',
                danger: true,
                onSelect: () => setPendingDelete(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Bell size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem current>Notification channels</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Notification channels"
        description="Configure where alerts are delivered — Telegram, Email, Slack, Teams, or custom webhooks."
        actions={
          <Button
            size="sm"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`${base}/notifications/create`)}
          >
            New channel
          </Button>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load channels"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The notification service is temporarily unavailable.
        </Alert>
      ) : isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Search channels…"
                startSlot={<Search />}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            }
          />
          <DataTable
            columns={columns}
            data={filteredChannels}
            getRowId={(c) => c.id}
            onRowClick={(c) => navigate(`${base}/notifications/${c.id}`)}
            emptyState={
              channels.length === 0 ? (
                <EmptyState
                  icon={<Bell size={28} />}
                  title="No notification channels"
                  description="Connect your first notification channel to start receiving alerts."
                  actions={
                    <Button
                      size="sm"
                      startIcon={<Plus size={15} />}
                      onClick={() => navigate(`${base}/notifications/create`)}
                    >
                      New channel
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Bell size={28} />}
                  title="No matching channels"
                  description="Try a different search term."
                />
              )
            }
            footer={
              filteredChannels.length
                ? `Showing ${filteredChannels.length} channel${filteredChannels.length === 1 ? '' : 's'}`
                : undefined
            }
          />
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        destructive
        title="Delete channel?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" and all its subscribers will be removed. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const ch = pendingDelete;
          del.mutate(ch.id, {
            onSuccess: () => {
              show({ tone: 'success', title: `Deleted "${ch.name}"` });
              setPendingDelete(null);
            },
            onError: (e) => {
              show({ tone: 'danger', title: (e as Error)?.message || 'Delete failed' });
              setPendingDelete(null);
            },
          });
        }}
      />
    </Container>
  );
}

